import React from 'react';
import { createRoot } from 'react-dom/client';

import { App } from 'antd';
import { BrowserRouter, generatePath, Navigate, Route, Routes } from 'react-router';

import { requireNonNull } from '@code-expert/prelude';
import { isExamUser } from '/imports/domain';
import { appRouter } from '/imports/modules/API/client';
import { handleInvitationsRouter } from '/imports/modules/Invitations/client';
import { projectBrowserRouter } from '/imports/modules/Project/client';
import { concatRoutes, formatRoute } from '/imports/modules/Routing';
import { RunTypedRoutes } from '/imports/modules/Routing/RunTypedRoutes';
import { Authorise, AuthorisePublic } from '/imports/ui/components/Authorise';
import { BrowserUpdate } from '/imports/ui/components/BrowserUpdate';
import { ErrorBoundary } from '/imports/ui/components/ErrorBoundary';
import { GlobalContextProvider, useGlobalContext } from '/imports/ui/components/GlobalContext';
import { Loading } from '/imports/ui/components/Loading';
import LockedUsers from '/imports/ui/components/LockedUsers';
import Notifications from '/imports/ui/components/Notifications/Notifications';
import RedirectNonLtiUsers from '/imports/ui/components/RedirectNonLtiUsers';
import { styled, ThemeProvider } from '/imports/ui/foundation/Theme';
import { TopNavLayout } from '/imports/ui/layouts/TopNavLayout';
import { adminRouter } from '/imports/ui/pages/admin/router';
import { adminRoute } from '/imports/ui/pages/admin/routes';
import { enrollRouter } from '/imports/ui/pages/enroll/router';
import { enroledRouter } from '/imports/ui/pages/enrolled/router';
import { enroledRoute } from '/imports/ui/pages/enrolled/routes';
import { examRouter } from '/imports/ui/pages/exam/router';
import { examStudentRoute } from '/imports/ui/pages/exam/routes';
import { ideRouter } from '/imports/ui/pages/ide/client';
import { ltiRouter } from '/imports/ui/pages/lti/router';
import { myCoursesRouter } from '/imports/ui/pages/myCourses/router';
import { myCoursesRoute } from '/imports/ui/pages/myCourses/routes';
import { myExamsRouter } from '/imports/ui/pages/myExams/router';
import { myGroupsRouter } from '/imports/ui/pages/myGroups/router';
import { myGroupsRoute } from '/imports/ui/pages/myGroups/routes';
import { printRouter } from '/imports/ui/pages/print/client';
import { reviewRouter } from '/imports/ui/pages/review/client';
import { userRouter } from '/imports/ui/pages/user/router';
import { indexRoute, infoRoute, loginDemoRoute, loginRoute } from './routes';

const Info = React.lazy(() => import('/imports/ui/pages/info/InfoPage'));
const LoginDemo = React.lazy(() => import('/imports/ui/pages/login/LoginDemo'));
const LoginEduId = React.lazy(() => import('/imports/ui/pages/login/LoginEduId'));
const IdeRedirect = React.lazy(() => import('/imports/ui/pages/ide/Redirect'));

const Entry = () => {
  const { user, userCapabilities, preferredEntry } = useGlobalContext();

  if (user != null) {
    if (isExamUser(user)) {
      const { examId } = user;
      return <Navigate to={formatRoute(examStudentRoute)({ examId })} />;
    }
    if (preferredEntry?.path != null) {
      const { path, courseUrl, semester, groupId } = preferredEntry;
      const to = generatePath('/:path?/:semester?/:courseUrl?/:groupId?/', {
        path: path?.replace(/^\/+/, '') ?? null,
        courseUrl: courseUrl ?? null,
        semester: semester ?? null,
        groupId: groupId ?? null,
      });
      return <Navigate to={to} />;
    }
    if (userCapabilities.includes('admin')) return <Navigate to={formatRoute(adminRoute)({})} />;
    if (userCapabilities.includes('courseAdmin'))
      return <Navigate to={formatRoute(myCoursesRoute)({})} />;
    if (userCapabilities.includes('groupAdmin'))
      return <Navigate to={formatRoute(myGroupsRoute)({})} />;
    if (userCapabilities.includes('enrolled'))
      return <Navigate to={formatRoute(enroledRoute)({})} />;
  }
  return <Navigate to={formatRoute(infoRoute)({})} />;
};

const globalRouter = concatRoutes([
  indexRoute.parser.map(() => <Entry />),
  infoRoute.parser.map(() => (
    <AuthorisePublic
      deny={['exam', 'lti']}
      layout={(children) => <TopNavLayout selected="none">{children}</TopNavLayout>}
    >
      <Info />
    </AuthorisePublic>
  )),
  loginRoute.parser.map(() => (
    <AuthorisePublic
      deny={['exam', 'lti']}
      layout={(children) => <TopNavLayout selected="none">{children}</TopNavLayout>}
    >
      <LoginEduId />
    </AuthorisePublic>
  )),
  loginDemoRoute.parser.map(() => (
    <AuthorisePublic
      deny={['exam', 'lti']}
      layout={(children) => <TopNavLayout selected="none">{children}</TopNavLayout>}
    >
      <LoginDemo />
    </AuthorisePublic>
  )),
]);

const CxApp = () => (
  <BrowserRouter>
    <BrowserUpdate />
    <Notifications />
    <RedirectNonLtiUsers />
    <LockedUsers />
    <React.Suspense fallback={<Loading />}>
      <Routes>
        <Route
          path="/solve/:taskId"
          element={
            <Authorise deny={['lti']}>
              <IdeRedirect />
            </Authorise>
          }
        />
        <Route
          path="/submissionFeedback/:submissionId"
          element={
            <Authorise>
              <IdeRedirect />
            </Authorise>
          }
        />
        <Route
          path="*"
          element={
            <RunTypedRoutes
              routes={[
                globalRouter,
                adminRouter,
                appRouter,
                enrollRouter,
                enroledRouter,
                examRouter,
                handleInvitationsRouter,
                ideRouter,
                ltiRouter,
                myCoursesRouter,
                myExamsRouter,
                myGroupsRouter,
                projectBrowserRouter,
                printRouter,
                reviewRouter,
                userRouter,
              ]}
            />
          }
        />
      </Routes>
    </React.Suspense>
  </BrowserRouter>
);

const StyledApp = styled(App, ({ tokens }) => ({
  height: '100%',
  backgroundColor: tokens.colorBgLayout,
}));

createRoot(requireNonNull(document.getElementById('app'), 'Expect element #app to exist')).render(
  <ThemeProvider>
    <StyledApp>
      <ErrorBoundary>
        <GlobalContextProvider>
          <CxApp />
        </GlobalContextProvider>
      </ErrorBoundary>
    </StyledApp>
  </ThemeProvider>,
);
